import { useEffect, useMemo, useState } from 'react';
import { create } from '@bufbuild/protobuf';
import { App, Alert, Button, Checkbox, Input, Modal, Switch, Tag } from 'antd';
import { Zap } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { maasApi } from '@/api/maas.api';
import { useMaasCredentials } from '../hooks/useMaasCredentials';
import {
  useCreateTenantFailover,
  useUpdateTenantFailover,
  useTenantFailovers,
} from '../hooks/useTenantFailovers';
import {
  CreateTenantFailoverRequestSchema,
  UpdateTenantFailoverRequestSchema,
} from '@/proto/agentplatform/httpv2/tenant_failover_pb';
import { type MaasModel } from '@/proto/agentplatform/v2/maas_pb';
import { formatTimestamp } from '@/utils/utils';
import { ApiError } from '@/api/errors';

interface Props {
  open: boolean;
  onClose: () => void;
}

function parsePool(text: string): string[] {
  return text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * 租户级模型 Failover 配置。
 * - 落库为独立的 TenantFailover 单例（每租户一条），backup_models 承载有序模型池。
 * - 顶部开关绑定 TenantFailover.enabled，随保存落库。
 * - 来源凭证仅用于探测可用模型，不承载 Failover 的 API Key；选中凭证时「测试并保存」
 *   会校验池内模型均在探测列表中，存在无效模型或探测失败则阻断保存。
 */
export default function TenantFailoverDialog({ open, onClose }: Props) {
  const { message } = App.useApp();

  // 独立拉全量以可靠定位单例 Failover 配置（不受外层分页影响）。
  const { data: failoverData } = useTenantFailovers(1, 100);
  const existing = useMemo(() => failoverData?.items?.[0], [failoverData?.items]);

  // 托管凭证仅用于探测可用模型列表。
  const { data: credData } = useMaasCredentials(1, 100);
  const managedCreds = useMemo(() => credData?.items ?? [], [credData?.items]);

  const createMut = useCreateTenantFailover();
  const updateMut = useUpdateTenantFailover();

  // 是否启用该 Failover 备用池（落库到 TenantFailover.enabled）。首次配置默认关闭。
  const [enabled, setEnabled] = useState(false);
  const [sourceCredId, setSourceCredId] = useState<string | undefined>(undefined);
  const selectedCred = useMemo(
    () => managedCreds.find((c) => c.id === sourceCredId),
    [managedCreds, sourceCredId]
  );
  const [pool, setPool] = useState<string[]>([]);
  const [poolText, setPoolText] = useState('');
  const [probeModels, setProbeModels] = useState<MaasModel[]>([]);
  const [probing, setProbing] = useState(false);
  const [saving, setSaving] = useState(false);
  // 模型选择弹窗：pickerSel 为弹窗内的临时勾选，确定后才回写 pool。
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSel, setPickerSel] = useState<string[]>([]);
  // 弹窗内按名称过滤展示（不影响已勾选项）。
  const [pickerKeyword, setPickerKeyword] = useState('');

  useEffect(() => {
    if (open) {
      const seed = existing?.backupModels ?? [];
      setPool(seed);
      setPoolText(seed.join(', '));
      setEnabled(existing ? existing.enabled : false);
      setSourceCredId(undefined);
      setProbeModels([]);
    }
  }, [open, existing]);

  useEffect(() => {
    if (open && !sourceCredId && managedCreds.length > 0) {
      setSourceCredId(managedCreds[0].id);
    }
  }, [open, sourceCredId, managedCreds]);

  // 下拉选中 → 同步保序数组与文本框。
  function onPickModels(vals: string[]) {
    setPool(vals);
    setPoolText(vals.join(', '));
  }

  // 文本框编辑 → 解析回数组（下拉与保存共用同一真值）。
  function onEditPoolText(text: string) {
    setPoolText(text);
    setPool(parsePool(text));
  }

  // 弹窗确定 → 保留 pool 中已存模型的原有顺序，新增模型按选项顺序追加。
  function onPickerOk() {
    const selSet = new Set(pickerSel);
    const kept = pool.filter((n) => selSet.has(n));
    const added = modelOptions
      .map((o) => o.value)
      .filter((n) => selSet.has(n) && !pool.includes(n));
    onPickModels([...kept, ...added]);
    setPickerOpen(false);
  }

  // 下拉选项 = 探测到的可用模型 ∪ 当前已选（保证未探测时已存池也能显示标签）。
  const modelOptions = useMemo(() => {
    const names = new Set<string>(probeModels.map((m) => m.name));
    pool.forEach((n) => names.add(n));
    return Array.from(names).map((name) => ({ value: name, label: name }));
  }, [probeModels, pool]);

  // 弹窗展示用：按关键字（模型名，忽略大小写）过滤；勾选状态不受过滤影响。
  const filteredModelOptions = useMemo(() => {
    const kw = pickerKeyword.trim().toLowerCase();
    if (!kw) return modelOptions;
    return modelOptions.filter((o) => o.value.toLowerCase().includes(kw));
  }, [modelOptions, pickerKeyword]);

  async function onProbe() {
    if (!sourceCredId) {
      message.info('先选择一个来源凭证');
      return;
    }
    setProbing(true);
    try {
      const res = await maasApi.listModelsByCredential(sourceCredId);
      setProbeModels(res.items);
      message.success(`拿到 ${res.items.length} 个模型`);
      // 获取模型后直接弹出选择弹窗（两列多选）。
      setPickerSel(pool);
      setPickerKeyword('');
      setPickerOpen(true);
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setProbing(false);
    }
  }

  async function onSave() {
    const cleaned = pool.map((s) => s.trim()).filter(Boolean);
    // 仅在启用 Failover 时才要求配置备用模型；关闭状态下允许清空保存。
    if (enabled && cleaned.length === 0) {
      message.error('请至少选择一个备用模型');
      return;
    }

    setSaving(true);
    try {
      // 「测试并保存」：用来源凭证探测可用模型列表，池内模型必须全部在列表中，否则不保存。
      if (sourceCredId && cleaned.length > 0) {
        let names: Set<string>;
        try {
          const res = await maasApi.listModelsByCredential(sourceCredId);
          names = new Set(res.items.map((m) => m.name));
        } catch {
          message.error('模型列表探测失败，保存失败');
          return;
        }
        const invalid = cleaned.filter((m) => !names.has(m));
        if (invalid.length > 0) {
          message.error(`${invalid.join('、')} 模型无效，保存失败`);
          return;
        }
      }

      if (existing) {
        await updateMut.mutateAsync(
          create(UpdateTenantFailoverRequestSchema, {
            id: existing.id,
            backupModels: cleaned,
            enabled,
          })
        );
      } else {
        await createMut.mutateAsync(
          create(CreateTenantFailoverRequestSchema, {
            backupModels: cleaned,
            enabled,
          })
        );
      }
      message.success('Failover 配置已保存');
      onClose();
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const saveLabel = sourceCredId ? '测试并保存' : '保存';

  return (
    <>
      <Modal
        title="租户级模型 Failover 配置"
        open={open}
        onCancel={onClose}
        footer={null}
        width={760}
        destroyOnClose
      >
        {/* 权限由后端 IAM 校验（agent:credential:tenant_failover:*），无权限时保存请求返回 403 并由全局弹窗提示。 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 4 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 16,
            }}
          >
            <div>
              <div style={{ fontWeight: 600 }}>启用租户级 Failover 备用池</div>
              <div style={{ fontSize: 12, color: 'var(--text-color-secondary)', marginTop: 4 }}>
                关闭后该备用池对所有 Agent 模板失效；开启后按下方模型范围与顺序降级切换。
              </div>
            </div>
            <Switch checked={enabled} onChange={setEnabled} />
          </div>

          <Alert
            type={enabled ? 'info' : 'warning'}
            showIcon
            message={
              enabled ? '租户限制将对所有 Agent 模板生效' : '当前不限制 Agent 开发者的备用模型范围'
            }
          />

          {enabled && (
            <>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>获取模型列表</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {/* 与「新增/编辑模型凭证」的默认模型下拉同款：面板内置搜索框，按凭证名称检索。 */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Select value={sourceCredId ?? ''} onValueChange={setSourceCredId}>
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="选择一个托管凭证以探测可用模型">
                          {selectedCred
                            ? `${selectedCred.name} · ${selectedCred.defaultModel || '未设默认模型'}`
                            : undefined}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent searchable searchPlaceholder="搜索凭证...">
                        {managedCreds.map((c) => (
                          <SelectItem key={c.id} value={c.id} searchKeywords={c.name}>
                            <span className="flex items-center justify-between w-full gap-2">
                              <span className="truncate">{c.name}</span>
                              <span className="text-xs text-muted-foreground shrink-0">
                                {c.defaultModel || '未设默认模型'}
                              </span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button icon={<Zap size={14} />} loading={probing} onClick={onProbe}>
                    获取模型
                  </Button>
                </div>
              </div>

              <div>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>
                  Failover 模型范围与请求顺序 <span style={{ color: '#ff4d4f' }}>*</span>
                </div>
                <div
                  style={{ fontSize: 12, color: 'var(--text-color-secondary)', marginBottom: 8 }}
                >
                  {pool.length > 0
                    ? `已选 ${pool.length} 个`
                    : modelOptions.length === 0
                      ? '先选择来源凭证并「获取模型」'
                      : '尚未选择备用模型'}
                </div>
                {pool.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    {pool.map((name) => (
                      <Tag
                        key={name}
                        closable
                        onClose={() => onPickModels(pool.filter((n) => n !== name))}
                        style={{ marginBottom: 4 }}
                      >
                        {name}
                      </Tag>
                    ))}
                  </div>
                )}
                <Input.TextArea
                  rows={2}
                  value={poolText}
                  onChange={(e) => onEditPoolText(e.target.value)}
                  placeholder="deepseek-v3.1, qwen3-235b-a22b, glm-4.5-air"
                />
                <div style={{ fontSize: 12, color: 'var(--text-color-secondary)', marginTop: 6 }}>
                  上方选择与下方输入框双向同步；按英文逗号分隔，从左到右依次请求。
                </div>
              </div>
            </>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              borderTop: '1px solid var(--border-color-split, #f0f0f0)',
              paddingTop: 16,
            }}
          >
            <span style={{ fontSize: 12, color: 'var(--text-color-secondary)' }}>
              {existing ? `最近更新：${formatTimestamp(existing.updatedAt)}` : '尚未配置'}
            </span>
            <Button type="primary" loading={saving} onClick={onSave}>
              {saveLabel}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        title="选择备用模型（可多选）"
        open={pickerOpen}
        onCancel={() => setPickerOpen(false)}
        onOk={onPickerOk}
        okText="确定"
        cancelText="取消"
        width={640}
        zIndex={1100}
      >
        {modelOptions.length === 0 ? (
          <Alert type="info" showIcon message="暂无可选模型，请先获取模型列表" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Input
              allowClear
              placeholder="按模型名称搜索"
              value={pickerKeyword}
              onChange={(e) => setPickerKeyword(e.target.value)}
            />
            {filteredModelOptions.length === 0 ? (
              <Alert type="info" showIcon message="无匹配模型，换个关键字试试" />
            ) : (
              <Checkbox.Group
                value={pickerSel}
                onChange={(vals) => {
                  // Checkbox.Group 只回报当前挂载项：合并被过滤隐藏的已勾选项，避免搜索时丢勾选。
                  const visible = new Set(filteredModelOptions.map((o) => o.value));
                  const hiddenKept = pickerSel.filter((v) => !visible.has(v));
                  setPickerSel([...hiddenKept, ...(vals as string[])]);
                }}
                style={{ width: '100%' }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 8,
                    maxHeight: 400,
                    overflowY: 'auto',
                  }}
                >
                  {filteredModelOptions.map((o) => (
                    <Checkbox key={o.value} value={o.value} style={{ marginLeft: 0 }}>
                      {o.label}
                    </Checkbox>
                  ))}
                </div>
              </Checkbox.Group>
            )}
            {pickerSel.length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-color-secondary)' }}>
                已勾选 {pickerSel.length} 个（含被搜索过滤隐藏的项）
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
